import type {InferUITool, UIMessage} from "ai";
import {z} from "zod";
import type {AppUsage} from "./usage";
import type {FeatureFlag as FeatureFlagDb} from "./db/schema";

export type KeyValue<T> = { [key: string]: T };

export const messageStatusValues = [
  "pending",
  "streaming",
  "complete",
  "error",
  "cancelled",
  "cancel_requested",
] as const;
export const MessageStatusSchema = z.enum(messageStatusValues);
export type MessageStatus = z.infer<typeof MessageStatusSchema>;

export const outputViolationDetectionSchema = z.object({
  detection_id: z.string(),
  detected: z.boolean(),
  codes: z.array(z.enum([
    "no_authoritative_evidence",
    "ungrounded_clinical_claim",
    "unsupported_medication",
    "unsupported_disorder",
    "recommendation_exceeds_evidence",
    "switchrx_response_not_verbatim",
  ])),
  summary: z.string(),
  evaluation_method: z.enum(["llm", "deterministic_switchrx"]),
}).strict();

export type OutputViolationDetection = z.infer<typeof outputViolationDetectionSchema>;

export const messageMetadataSchema = z.object({
  createdAt: z.string(),
  isDeidentified: z.boolean().optional(),
  status: MessageStatusSchema.optional(),
});

export type MessageMetadata = z.infer<typeof messageMetadataSchema>;

export type ChatTools = {};

export type CustomUIDataTypes = {
  appendMessage: string;
  usage: AppUsage;
  deidentifiedMessage: {
    messageId: string;
    deidentifiedContent: string;
    isDeidentified: boolean;
  };
  errorMetadata: {
    status: "error";
  };
  responseReplacement: {
    artifactId: string;
    text: string;
  };
  streamFlush: {};  // Explicit flush trigger with minimal payload
  taskId: { taskId: string };  // A2A task ID for cancel-task RPC
};

export type ChatMessage = UIMessage<
  MessageMetadata,
  CustomUIDataTypes,
  ChatTools
>;

export type VisibilityType = "private" | "public";

export type Attachment = {
  name: string;
  url: string;
  contentType: string;
};

export enum UserRole {
  ALPHA = "RAPIDS-alpha-user",
  BETA = "RAPIDS-beta-user",
  DEFAULT_USER = "default_user",
}

export enum FeatureFlagName {
  USE_AGENTIC_ORCHESTRATOR = "use_agentic_orchestrator",
  USE_TREATMENT_GUIDANCE = "use_treatment_guidance",
  USE_PANIC_DISORDER = "use_panic_disorder",
  USE_PTSD = "use_ptsd",
  USE_OCD = "use_ocd",
}

export type FeatureFlag = FeatureFlagDb;

export interface FeatureFlagsData {
  userRole: UserRole;
  featureFlags: KeyValue<FeatureFlag>;
}

export type UnauthenticatedCode = 'unauthenticated' | 'session_invalidated';

export interface DisclaimerContent {
  title: string;
  paragraphs: string[];
}

export interface DisclaimerData {
  isAcknowledged: boolean;
  acknowledgedAt: string | null;
  currentVersion: string;
  content: DisclaimerContent;
}

/**
 * Client configuration provided by the /api/config endpoint.
 * Contains public configuration values needed by the client.
 */
export interface ClientConfig {
  basePath: string;
  auth0Domain: string;
  auth0ClientId: string;
  recaptchaSiteId: string;
  rapidsVerticalName: string;
  logsUrl: string;
  maintenanceMode: boolean;
}

export const SUPPORT_FORM_SUBJECT_LENGTH_MAX = 500;
export const SUPPORT_FORM_DESCRIPTION_LENGTH_MAX = 5000;
